// src/components/pages/admin-dash/ReportBarList.jsx
import React from "react";
import Icon from "../../ui/Icon";

/**
 * Hand-built horizontal bar list — no charting dependency (none exists
 * in this project; this is the one report that genuinely benefits from
 * a visual over a plain table, so it's built from CSS rather than
 * pulling in a library for a single chart).
 *
 * Single series (bar length = `count`), so per dataviz convention this
 * needs no legend/hue ramp — one flat accent color throughout. The
 * count is a direct label at the bar's end (always visible, nothing is
 * hover-gated); `title` adds a native tooltip for the secondary value
 * on hover/focus without building a custom tooltip layer for a 2-6 bar
 * internal report.
 *
 * Props:
 *   bars: [{ key, label, count, secondaryValue? }]
 *   valueFormatter(count) -> string
 *   secondaryFormatter(secondaryValue) -> string
 *   emptyMessage: string
 */
export default function ReportBarList({
  bars,
  valueFormatter = (n) => n.toLocaleString(),
  secondaryFormatter = (n) => n.toLocaleString(),
  emptyMessage = "No data for this filter yet.",
}) {
  if (!bars || bars.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-2">
        <Icon name="chart-bar" size={26} style={{ color: "var(--txt-3)" }} />
        <p className="text-[13px]" style={{ color: "var(--txt-2)" }}>{emptyMessage}</p>
      </div>
    );
  }

  const maxCount = Math.max(...bars.map((b) => b.count || 0), 1);

  return (
    <div className="space-y-3">
      {bars.map((b) => {
        const pct = Math.max(0, Math.min(100, Math.round(((b.count || 0) / maxCount) * 100)));
        const hasSecondary = b.secondaryValue != null;
        const tooltip = `${b.label}: ${valueFormatter(b.count || 0)}${hasSecondary ? ` · ${secondaryFormatter(b.secondaryValue)}` : ""}`;

        return (
          <div key={b.key} className="flex items-center gap-3">
            <div
              className="w-24 shrink-0 text-[12px] font-medium text-right"
              style={{ color: "var(--txt)" }}
            >
              {b.label}
            </div>

            <div
              tabIndex={0}
              title={tooltip}
              aria-label={tooltip}
              className="flex-1 min-w-0 rounded-md outline-none"
              style={{ background: "var(--surf-2)", height: 20 }}
            >
              <div
                className="h-full transition-[width] duration-300 ease-out"
                style={{
                  width: `${pct}%`,
                  background: "var(--gold)",
                  borderTopRightRadius: 4,
                  borderBottomRightRadius: 4,
                }}
              />
            </div>

            <div className="w-32 shrink-0 text-[12px] tabular-nums" style={{ color: "var(--txt-2)" }}>
              {valueFormatter(b.count || 0)}
              {hasSecondary && (
                <span className="ml-1.5" style={{ color: "var(--txt-3)" }}>
                  · {secondaryFormatter(b.secondaryValue)}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
